import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Chip, Collapse } from '@mui/material';
import {
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const TemplateInformation = ({
  template,
  templateId,
  parseTemplateVariables,
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const templateVariables = template?.template
    ? parseTemplateVariables(template.template)
    : [];

  const description = template?.description || 'No description available';
  const isLongDescription = description.length > 200;

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <InfoIcon sx={{ mr: 1 }} />
            Template Information
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary">
            {template?.created_at
              ? new Date(template.created_at).toLocaleDateString()
              : 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            v{template?.version || '1.0.0'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Template Name
          </Typography>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {template?.name || 'Unnamed Template'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            {templateId}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Template Variables
          </Typography>
          {templateVariables.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {templateVariables.map(variable => (
                <Chip
                  key={variable}
                  label={`{{${variable}}}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No variables found
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Description
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Collapse in={descriptionExpanded} collapsedSize={100}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {description}
            </Typography>
          </Collapse>
          {isLongDescription && (
            <Button
              size="small"
              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              endIcon={
                descriptionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{
                mt: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'primary.main',
                minWidth: 'auto',
                px: 1,
              }}
            >
              {descriptionExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

TemplateInformation.propTypes = {
  template: PropTypes.object,
  templateId: PropTypes.string.isRequired,
  parseTemplateVariables: PropTypes.func.isRequired,
};

export default TemplateInformation;
